import httpRequest from "@/api/index";
import type { RequestAxiosConfig } from "@/api/index";
import type { Page } from "../../../../common/types/base/BaseEntity";
import type {
    ProductTypePriceWarning,
    ProductTypePriceWarningBatchUpdateForm,
    ProductTypePriceWarningCreateForm,
    ProductTypePriceWarningCurrentPriceParams,
    ProductTypePriceWarningCurrentPriceVo,
    ProductTypePriceWarningGroupVo,
    ProductTypePriceWarningPagingParams,
    ProductTypePriceWarningThresholdForm,
    ProductTypePriceWarningUpdateForm,
    ProductTypePriceWarningVo
} from "@zhihaoscm/entity";

type ProductTypePriceWarningBatchStateForm = {
    /**
     * 当前品类下需要统一关闭或启用的全部价格类型。
     */
    queryTypes: number[];
};

class ProductTypePriceWarningService {
    private readonly path: string;
    private readonly service: typeof httpRequest;

    constructor(path: string, service: typeof httpRequest) {
        this.path = path;
        this.service = service;
    }

    public async paging(params: ProductTypePriceWarningPagingParams): Promise<Page<ProductTypePriceWarningGroupVo>> {
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }

    public async getDetail(id: number | string, silentError = false): Promise<ProductTypePriceWarningVo> {
        // 删除后的价格提醒消息会用详情失败作为兜底信号，避免全局错误提示重复弹出。
        const config: RequestAxiosConfig = { silentError };
        const { data } = await this.service.get(`${this.path}/${id}`, config);
        return data;
    }

    public async getByProductType(productTypeId: string): Promise<ProductTypePriceWarningVo[]> {
        const { data } = await this.service.get(`${this.path}/product-type/${productTypeId}`);
        return data;
    }

    public async getCurrentPrice(
        params: ProductTypePriceWarningCurrentPriceParams
    ): Promise<ProductTypePriceWarningCurrentPriceVo> {
        const { data } = await this.service.get(`${this.path}/current-price`, { params });
        return data;
    }

    public async create(form: ProductTypePriceWarningCreateForm): Promise<ProductTypePriceWarning[]> {
        const { data } = await this.service.post(this.path, form);
        return data;
    }

    public async update(
        id: number | string,
        form: ProductTypePriceWarningUpdateForm
    ): Promise<ProductTypePriceWarning> {
        const { data } = await this.service.put(`${this.path}/${id}`, form);
        return data;
    }

    public async updateByProductType(
        productTypeId: string,
        form: ProductTypePriceWarningBatchUpdateForm
    ): Promise<ProductTypePriceWarningVo[]> {
        const { data } = await this.service.put(`${this.path}/product-type/${productTypeId}`, form);
        return data;
    }

    public async enable(id: number | string): Promise<ProductTypePriceWarning> {
        const { data } = await this.service.put(`${this.path}/${id}/enable`, {});
        return data;
    }

    public async enableByProductType(
        productTypeId: string,
        form: ProductTypePriceWarningBatchStateForm
    ): Promise<ProductTypePriceWarningVo[]> {
        const { data } = await this.service.put(`${this.path}/product-type/${productTypeId}/enable`, form);
        return data;
    }

    public async disable(id: number | string): Promise<ProductTypePriceWarning> {
        const { data } = await this.service.put(`${this.path}/${id}/disable`, {});
        return data;
    }

    public async disableByProductType(
        productTypeId: string,
        form: ProductTypePriceWarningBatchStateForm
    ): Promise<ProductTypePriceWarningVo[]> {
        const { data } = await this.service.put(`${this.path}/product-type/${productTypeId}/disable`, form);
        return data;
    }

    public async delete(id: number | string): Promise<ProductTypePriceWarning> {
        const { data } = await this.service.delete(`${this.path}/${id}`);
        return data;
    }

    public async deleteByProductType(productTypeId: string): Promise<ProductTypePriceWarningVo[]> {
        const { data } = await this.service.delete(`${this.path}/product-type/${productTypeId}`);
        return data;
    }

    public async setTop(id: number | string, top: number): Promise<ProductTypePriceWarning> {
        const { data } = await this.service.put(`${this.path}/${id}/top?top=${top}`, {});
        return data;
    }

    public async updateThreshold(
        id: number | string,
        form: ProductTypePriceWarningThresholdForm
    ): Promise<ProductTypePriceWarning> {
        const { data } = await this.service.put(`${this.path}/${id}/threshold`, form);
        return data;
    }
}

export const productTypePriceWarningService = new ProductTypePriceWarningService(
    "/product-type-price-warning",
    httpRequest
);
